import { useId } from 'react';

/**
 * Isotipo Togados — reconstrucción vectorial desde el Brandbook Togados v2.
 *
 * Componentes del símbolo, según el brandbook (p. 4): el individuo como
 * persona, la toga, el calígrafo, la espada de la victoria y la justicia, y el
 * hexágono en concordancia a la nube de datos.
 *
 * ⚠️ Esto es una RECONSTRUCCIÓN a partir del PDF del brandbook, que sólo
 * contiene el logo rasterizado. Es geométricamente fiel y escala sin pérdida,
 * pero no es el vector oficial. Sustitúyalo por el SVG maestro en cuanto el
 * equipo de marca lo entregue — este archivo es el único punto a tocar.
 *
 * La toga se construye con una máscara en lugar de un path con agujeros: los
 * vacíos (la abertura en V del cuello y las dos muescas que producen la lectura
 * de paréntesis ⟨ ⟩) quedan REALMENTE transparentes, no pintados de blanco. Por
 * eso el mismo componente funciona sobre fondo claro y oscuro sin recolorear —
 * la norma del brandbook que prohíbe aplanar el logo a color único perdiendo el
 * detalle interno (p. 9) se cumple por construcción.
 */

export type LogoVariant = 'brand' | 'white' | 'black';

interface IsotipoProps {
  readonly variant?: LogoVariant;
  readonly className?: string;
}

const PALETTE: Record<LogoVariant, { body: string; tie: string }> = {
  brand: { body: '#013E9A', tie: '#000000' },
  white: { body: '#FFFFFF', tie: '#FFFFFF' },
  black: { body: '#000000', tie: '#000000' },
};

export function Isotipo({ variant = 'brand', className }: IsotipoProps) {
  // Un id único por instancia: sin esto, dos logos en la misma página
  // colisionarían en la referencia de la máscara y el segundo se rompería.
  const maskId = useId();
  const palette = PALETTE[variant];

  return (
    <svg
      viewBox="0 0 100 104"
      className={className}
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <mask id={maskId}>
          {/* Blanco = se pinta; negro = se recorta. */}
          <circle cx="50" cy="68" r="34" fill="#fff" />

          {/* Abertura en V del cuello, hasta la punta inferior de la toga.
              El ancho superior (32→68) fija el grosor de las solapas: abrirlo
              más adelgaza la toga y la aleja del peso visual del brandbook. */}
          <path d="M32 30 L68 30 L50 103 Z" fill="#000" />

          {/* Muescas laterales: los paréntesis ⟨ ⟩ que enmarcan la figura. */}
          <path
            d="M29 49 A 28 28 0 0 0 29 87"
            fill="none"
            stroke="#000"
            strokeWidth="4.5"
          />
          <path
            d="M71 49 A 28 28 0 0 1 71 87"
            fill="none"
            stroke="#000"
            strokeWidth="4.5"
          />
        </mask>
      </defs>

      {/* Cabeza: el individuo como persona. */}
      <circle cx="50" cy="15" r="14" fill={palette.body} />

      {/* Toga. */}
      <circle cx="50" cy="68" r="34" fill={palette.body} mask={`url(#${maskId})`} />

      {/* Corbata / espada: nudo superior y hoja apuntando hacia abajo. */}
      <path
        fill={palette.tie}
        d="M50 38 L57 46 L53.5 53 L56 57 L54 86 L50 95 L46 86 L44 57 L46.5 53 L43 46 Z"
      />
    </svg>
  );
}
